/**
 * This file handles the necessary JS logic for the accordion functionality for the program plan block.
 *
 * The same code is used for both the frontend and backend.
 */

const CLASS_ACCORDION_CONTENT_HIDDEN = 'is-content-hidden';
const CLASS_ACCORDION_HEADER_OPEN = 'is-accordion-header-open';
const CLASS_ACCORDION_ICON_OPEN = 'program-plan__accordions-icon--open';
const CLASS_ACCORDION_ICON_CLOSED = 'program-plan__accordions-icon--closed';
const CLASS_ACCORDION_WRAPPER_CLOSED = 'program-plan__accordion-wrapper--closed';
const CLASS_ACCORDION_WRAPPER_OPEN = 'program-plan__accordion-wrapper--open';

const SELECTOR_ACCORDION_ICON = '.js-program-plan-accordions-icon';

/**
 * Initialize accordion functionality for program plan blocks.
 *
 * @param {string} selector The CSS selector for accordion containers.
 * @return {void}
 */
export function initAccordion( selector = '.js-program-plan-accordions' ) {
	const accordions = document.querySelectorAll( selector );

	if ( ! accordions.length ) {
		return;
	}

	accordions.forEach( accordion => {
		const headers = accordion.querySelectorAll( '.js-accordion-header' );

		headers.forEach( header => {
			// Remove any existing event listeners
			header.removeEventListener( 'click', toggleAccordion );

			// Add click event listener
			header.addEventListener( 'click', toggleAccordion );

			// Setup enter key support for accessibility
			header.removeEventListener( 'keydown', keydownEventListener );
			header.addEventListener( 'keydown', keydownEventListener );
		} );
	} );
}

/**
 * Event listener for keydown events on accordion headers.
 *
 * @param {KeyboardEvent} event The keydown event.
 * @return {void}
 */
function keydownEventListener( event ) {
	if ( event.key === 'Enter' || event.key === ' ' ) {
		event.preventDefault();
		toggleAccordion( event );
	}
}

/**
 * Toggle accordion item open/closed.
 *
 * @param {Event}            event   The click event.
 * @param {HTMLElement|null} element The accordion header element, if provided.
 * @return {void}
 */
function toggleAccordion( event ) {
	let header = event.currentTarget;

	if ( ! header && event.target instanceof HTMLElement ) {
		header = event.target;
	}
	const content = header.nextElementSibling;
	const isExpanded = header.getAttribute( 'aria-expanded' ) === 'true';

	// Toggle current accordion item
	if ( content && content.classList.contains( 'js-accordion-content' ) ) {
		if ( isExpanded ) {
			// Close accordion
			closeAccordion( header, content );
		} else {
			// Open accordion
			openAccordion( header, content );
		}
		// clear focus state if user is not using keyboard.
		if ( ! ( event instanceof KeyboardEvent ) ) {
			header.blur();
		}
	}
}

/**
 * Open a specific accordion item.
 *
 * @param {HTMLElement} header  The accordion header element.
 * @param {HTMLElement} content The accordion content element.
 * @return {void}
 */
const openAccordion = ( header, content ) => {
	const wrapper = header.parentElement;
	const icon = header.querySelector( SELECTOR_ACCORDION_ICON );
	content.classList.remove( CLASS_ACCORDION_CONTENT_HIDDEN );
	content.setAttribute( 'aria-hidden', 'false' );
	header.setAttribute( 'aria-expanded', 'true' );
	header.classList.add( CLASS_ACCORDION_HEADER_OPEN );

	if ( wrapper ) {
		wrapper.classList.remove( CLASS_ACCORDION_WRAPPER_CLOSED );
		wrapper.classList.add( CLASS_ACCORDION_WRAPPER_OPEN );
	}

	if ( icon ) {
		icon.classList.remove( CLASS_ACCORDION_ICON_CLOSED );
		icon.classList.add( CLASS_ACCORDION_ICON_OPEN );
	}
};

/**
 * Close a specific accordion item.
 *
 * @param {HTMLElement} header  The accordion header element.
 * @param {HTMLElement} content The accordion content element.
 * @return {void}
 */
const closeAccordion = ( header, content ) => {
	const wrapper = header.parentElement;
	const icon = header.querySelector( SELECTOR_ACCORDION_ICON );
	content.classList.add( CLASS_ACCORDION_CONTENT_HIDDEN );
	content.setAttribute( 'aria-hidden', 'true' );
	header.setAttribute( 'aria-expanded', 'false' );
	header.classList.remove( CLASS_ACCORDION_HEADER_OPEN );

	if ( wrapper ) {
		wrapper.classList.remove( CLASS_ACCORDION_WRAPPER_OPEN );
		wrapper.classList.add( CLASS_ACCORDION_WRAPPER_CLOSED );
	}

	if ( icon ) {
		icon.classList.remove( CLASS_ACCORDION_ICON_OPEN );
		icon.classList.add( CLASS_ACCORDION_ICON_CLOSED );
	}
};

/**
 * Initialize accordions when DOM is ready.
 */
document.addEventListener( 'DOMContentLoaded', () => {
	initAccordion();
} );
